import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../config/region';
import { isInLineBrowser, getLiffId } from '../services/liffService';

// LINE SVG logo
const LineLogo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.494.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
  </svg>
);

// Zalo logo (simple Z in circle)
const ZaloLogo = () => (
  <svg width="22" height="22" viewBox="0 0 48 48" fill="none">
    <circle cx="24" cy="24" r="24" fill="white" fillOpacity="0.3" />
    <text x="24" y="32" textAnchor="middle" fill="white" fontSize="22" fontWeight="bold" fontFamily="Arial">Z</text>
  </svg>
);

const JoinVenuePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const roleLabel = (role) => (role ? t(`organizer.role_${role}`, { defaultValue: role }) : role);

  // Page states: 'loading-invite' | 'invite-ready' | 'accepting' | 'success' | 'error'
  const [pageState, setPageState] = useState('loading-invite');
  const [inviteInfo, setInviteInfo] = useState(null); // { venueName, role, expiresAt, expired, used }
  const [errorText, setErrorText] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [acceptedRole, setAcceptedRole] = useState('');
  const [acceptedVenueName, setAcceptedVenueName] = useState('');

  const token = searchParams.get('token');

  // Step 1: On mount, fetch invite info (public endpoint — no auth needed)
  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorText(t('organizer.invalidInviteLink'));
      setErrorDetail(t('organizer.linkMissingToken'));
      return;
    }

    const fetchInviteInfo = async () => {
      try {
        const apiBaseUrl = getApiBaseUrl();
        const res = await fetch(`${apiBaseUrl}/invite-info/${encodeURIComponent(token)}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setPageState('error');
          setErrorText(t('organizer.inviteNotFound'));
          setErrorDetail(t('organizer.inviteLinkInvalidAskAgent'));
          return;
        }

        if (data.used) {
          setPageState('error');
          setErrorText(t('organizer.inviteAlreadyUsed'));
          setErrorDetail(t('organizer.inviteAlreadyAcceptedContactAgent'));
          return;
        }

        if (data.expired) {
          setPageState('error');
          setErrorText(t('organizer.inviteExpired'));
          setErrorDetail(t('organizer.inviteExpiredAskFreshQr'));
          return;
        }

        setInviteInfo(data);

        // Check if already logged in — if yes, auto-accept
        const authToken = localStorage.getItem('venueapp_token')
          || localStorage.getItem('venue_app_token')
          || localStorage.getItem('token');

        if (authToken) {
          acceptInvite(token, authToken);
        } else {
          setPageState('invite-ready');
        }
      } catch (err) {
        console.error('[JoinVenue] fetchInviteInfo error:', err);
        setPageState('error');
        setErrorText(t('organizer.couldNotLoadInvite'));
        setErrorDetail(t('organizer.somethingWentWrongLoadingInvite'));
      }
    };

    fetchInviteInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Accept invite with JWT
  const acceptInvite = async (inviteToken, authToken) => {
    setPageState('accepting');
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/auth/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ token: inviteToken })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('organizer.failedToAcceptInvite'));
      }

      setAcceptedVenueName(data.venueName || inviteInfo?.venueName || '');
      setAcceptedRole(data.role || inviteInfo?.role || 'staff');
      setPageState('success');
      toast.success(t('organizer.youHaveBeenAddedToVenue'));
    } catch (err) {
      console.error('[JoinVenue] acceptInvite error:', err);
      setPageState('error');
      if (err.message.toLowerCase().includes('expired')) {
        setErrorText(t('organizer.inviteExpired'));
        setErrorDetail(t('organizer.inviteExpiredAskNewOne'));
      } else if (err.message.toLowerCase().includes('used') || err.message.toLowerCase().includes('claimed')) {
        setErrorText(t('organizer.inviteAlreadyUsed'));
        setErrorDetail(t('organizer.inviteAlreadyAcceptedContactAgent'));
      } else {
        setErrorText(t('organizer.couldNotAcceptInviteShort'));
        setErrorDetail(err.message || t('organizer.somethingWentWrongTryAgainContactAgent'));
      }
    }
  };

  // User taps "Log in with LINE"
  // Standard OAuth works in normal browsers (Chrome/Safari).
  // LINE's own browser blocks web OAuth — use LIFF instead so scanning the
  // QR code with the LINE camera or opening the link inside LINE still works.
  const handleLineLogin = () => {
    // Always persist invite token — survives both OAuth and LIFF round-trips
    localStorage.setItem('pending_invite_token', token);
    sessionStorage.setItem('pending_invite_token', token);

    if (isInLineBrowser() && getLiffId()) {
      // Inside LINE browser → route through LIFF
      // Append /login so LIFF redirects to thai.honestlee.app/login where
      // useLiffAutoLogin hook processes the auth code and exchanges for JWT.
      // Without /login, LIFF redirects to / which doesn't have the hook.
      const liffId = getLiffId();
      window.location.href = `https://liff.line.me/${liffId}/login`;
    } else {
      // Normal browser → standard OAuth redirect
      const apiBaseUrl = getApiBaseUrl();
      const backendRoot = apiBaseUrl.replace(/\/api$/, '');
      window.location.href = `${backendRoot}/auth/line`;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '16px'
    }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        style={{ maxWidth: 420, width: '100%' }}
      >
        <div style={{
          textAlign: 'center',
          padding: '2.5rem 2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          borderRadius: '1rem',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          {/* Logo */}
          <div style={{ fontSize: 28, fontWeight: 'bold', color: 'white', marginBottom: 28, letterSpacing: '-0.5px' }}>
            HonestLee
          </div>

          {/* Loading invite info */}
          {pageState === 'loading-invite' && (
            <>
              <Spinner />
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, margin: 0 }}>{t('organizer.loadingInviteDots')}</p>
            </>
          )}

          {/* Invite landing — user needs to log in */}
          {pageState === 'invite-ready' && inviteInfo && (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px', fontSize: 28
              }}>
                🏠
              </div>

              <h2 style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
                {t('organizer.youveBeenInvited')}
              </h2>

              {inviteInfo.venueName && (
                <p style={{ color: '#fef3c7', fontSize: 16, margin: '0 0 6px', fontWeight: 600 }}>
                  {inviteInfo.venueName}
                </p>
              )}

              {inviteInfo.role && (
                <span style={{
                  display: 'inline-block',
                  padding: '3px 14px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  background: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  marginBottom: 28
                }}>
                  {roleLabel(inviteInfo.role)}
                </span>
              )}

              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: '0 0 24px', lineHeight: 1.5 }}>
                {inviteInfo.platform === 'zalo'
                  ? t('organizer.logInWithZaloToAccept')
                  : t('organizer.logInWithLineToAccept')}
              </p>

              {/* Primary CTA — platform-aware auth button */}
              {inviteInfo.platform === 'zalo' ? (
                <button
                  onClick={() => {
                    localStorage.setItem('pending_invite_token', token);
                    sessionStorage.setItem('pending_invite_token', token);
                    const apiBaseUrl = getApiBaseUrl();
                    const backendRoot = apiBaseUrl.replace(/\/api$/, '');
                    window.location.href = `${backendRoot}/auth/zalo?state=invite:${token}`;
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '14px 20px',
                    backgroundColor: '#0068FF',
                    color: 'white',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: 'pointer',
                    letterSpacing: '-0.2px',
                    boxShadow: '0 4px 14px rgba(0, 104, 255, 0.4)'
                  }}
                >
                  <ZaloLogo />
                  {t('organizer.logInWithZalo')}
                </button>
              ) : (
                <button
                  onClick={handleLineLogin}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '14px 20px',
                    backgroundColor: '#06C755',
                    color: 'white',
                    border: 'none',
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 700,
                    cursor: 'pointer',
                    letterSpacing: '-0.2px',
                    boxShadow: '0 4px 14px rgba(6, 199, 85, 0.4)'
                  }}
                >
                  <LineLogo />
                  {t('organizer.logInWithLine')}
                </button>
              )}

              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, margin: '16px 0 0' }}>
                {inviteInfo.platform === 'zalo'
                  ? t('organizer.opensZaloForAuth')
                  : isInLineBrowser()
                    ? t('organizer.youreInLineTapAbove')
                    : t('organizer.worksBestFromLineApp')}
              </p>
            </>
          )}

          {/* Accepting invite (logged in, processing) */}
          {pageState === 'accepting' && (
            <>
              <Spinner />
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, margin: 0 }}>
                {t('organizer.acceptingInviteDots')}
              </p>
            </>
          )}

          {/* Success */}
          {pageState === 'success' && (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                backgroundColor: 'rgba(16, 185, 129, 0.8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>

              <h2 style={{ color: 'white', fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>
                {t('organizer.youreIn')}
              </h2>

              {acceptedVenueName && (
                <p style={{ color: '#fef3c7', fontSize: 16, margin: '0 0 6px', fontWeight: 600 }}>
                  {acceptedVenueName}
                </p>
              )}

              {acceptedRole && (
                <span style={{
                  display: 'inline-block',
                  padding: '3px 14px',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  background: 'rgba(255,255,255,0.25)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  marginBottom: 24
                }}>
                  {roleLabel(acceptedRole)}
                </span>
              )}

              <button
                onClick={() => navigate(acceptedRole ? '/venue-dashboard' : '/', { replace: true })}
                style={{
                  padding: '11px 28px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: 4
                }}
              >
                {acceptedRole ? t('organizer.goToYourDashboard') : t('organizer.continueToHonestLee')}
              </button>
            </>
          )}

          {/* Error */}
          {pageState === 'error' && (
            <>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                backgroundColor: 'rgba(239, 68, 68, 0.8)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </div>

              <h2 style={{ color: 'white', fontSize: 20, fontWeight: 700, margin: '0 0 10px' }}>{errorText || t('organizer.somethingWentWrong')}</h2>
              {errorDetail && (
                <p style={{ color: '#fef3c7', fontSize: 14, margin: '0 0 24px', lineHeight: 1.5 }}>{errorDetail}</p>
              )}

              <button
                onClick={() => navigate('/', { replace: true })}
                style={{
                  padding: '11px 28px',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {t('organizer.goToHome')}
              </button>
            </>
          )}
        </div>
      </motion.div>

      <style>{`@keyframes spin {0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </div>
  );
};

function Spinner() {
  return (
    <div style={{
      width: '3rem', height: '3rem',
      border: '4px solid rgba(255, 255, 255, 0.3)',
      borderTop: '4px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      margin: '0 auto 20px'
    }} />
  );
}

export default JoinVenuePage;
